import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Users, Mail, Image, LogOut, Loader2, Upload, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [isAddingGallery, setIsAddingGallery] = useState(false);
  const [newGalleryItem, setNewGalleryItem] = useState({
    title: '',
    category: 'Paint Correction',
    description: '',
    before_image: '',
    after_image: '',
  });

  useEffect(() => {
    checkAuth();
    fetchData();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, quotesRes, contactsRes, galleryRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, getAuthHeaders()),
        axios.get(`${API}/bookings`, getAuthHeaders()),
        axios.get(`${API}/quotes`, getAuthHeaders()),
        axios.get(`${API}/contacts`, getAuthHeaders()),
        axios.get(`${API}/gallery`),
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleImageUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewGalleryItem(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddGalleryItem = async () => {
    if (!newGalleryItem.title || !newGalleryItem.before_image || !newGalleryItem.after_image) {
      toast.error('Please fill all required fields and upload both images');
      return;
    }

    setIsAddingGallery(true);
    try {
      await axios.post(`${API}/gallery`, newGalleryItem, getAuthHeaders());
      toast.success('Gallery item added successfully');
      setNewGalleryItem({
        title: '',
        category: 'Paint Correction',
        description: '',
        before_image: '',
        after_image: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error adding gallery item:', error);
      toast.error('Failed to add gallery item');
    } finally {
      setIsAddingGallery(false);
    }
  };

  const handleDeleteGalleryItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gallery item?')) return;

    try {
      await axios.delete(`${API}/gallery/${id}`, getAuthHeaders());
      toast.success('Gallery item deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      toast.error('Failed to delete gallery item');
    }
  };

  if (isLoading) {
    return (
      <div data-testid="admin-loading" className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_mobile-detailing-11/artifacts/n9c26bxp_Untitled%20design.png" 
              alt="ONYXA Car Polishing & Detailing" 
              className="h-14 w-auto"
            />
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div data-testid="stats-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold text-white">{stats?.total_bookings || 0}</p>
              </div>
              <Calendar className="text-red-500" size={32} />
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Quote Requests</p>
                <p className="text-3xl font-bold text-white">{stats?.total_quotes || 0}</p>
              </div>
              <Users className="text-red-500" size={32} />
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Contact Messages</p>
                <p className="text-3xl font-bold text-white">{stats?.total_contacts || 0}</p>
              </div>
              <Mail className="text-red-500" size={32} />
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Gallery Items</p>
                <p className="text-3xl font-bold text-white">{stats?.total_gallery_items || 0}</p>
              </div>
              <Image className="text-red-500" size={32} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" data-testid="admin-tabs" className="w-full">
          <TabsList className="bg-gray-900 border-gray-800">
            <TabsTrigger value="bookings" data-testid="bookings-tab" className="data-[state=active]:bg-red-600">Bookings</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="quotes-tab" className="data-[state=active]:bg-red-600">Quotes</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="contacts-tab" className="data-[state=active]:bg-red-600">Contacts</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="gallery-tab" className="data-[state=active]:bg-red-600">Gallery</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" data-testid="bookings-content">
            <div className="glass-card p-6">
              <h2 className="text-2xl font-bold mb-6">Bookings</h2>
              <div className="space-y-4">
                {bookings.length === 0 ? (
                  <p className="text-gray-400">No bookings yet</p>
                ) : (
                  bookings.map((booking) => (
                    <div key={booking.id} data-testid={`booking-${booking.id}`} className="bg-gray-900 p-4 rounded-lg">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Client</p>
                          <p className="text-white font-semibold">{booking.client_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Service</p>
                          <p className="text-white">{booking.service}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Date & Time</p>
                          <p className="text-white">{booking.date} at {booking.time}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Vehicle</p>
                          <p className="text-white">{booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-gray-400 text-sm">Contact: {booking.client_email} | {booking.client_phone}</p>
                        {booking.notes && <p className="text-gray-400 text-sm mt-1">Notes: {booking.notes}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes" data-testid="quotes-content">
            <div className="glass-card p-6">
              <h2 className="text-2xl font-bold mb-6">Quote Requests</h2>
              <div className="space-y-4">
                {quotes.length === 0 ? (
                  <p className="text-gray-400">No quote requests yet</p>
                ) : (
                  quotes.map((quote) => (
                    <div key={quote.id} data-testid={`quote-${quote.id}`} className="bg-gray-900 p-4 rounded-lg">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Client</p>
                          <p className="text-white font-semibold">{quote.client_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Service</p>
                          <p className="text-white">{quote.service_interested}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Vehicle</p>
                          <p className="text-white">{quote.vehicle_year} {quote.vehicle_make} {quote.vehicle_model}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Photos</p>
                          <p className="text-white">{quote.photos?.length || 0} uploaded</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-gray-400 text-sm">Contact: {quote.client_email} | {quote.client_phone}</p>
                        {quote.message && <p className="text-gray-400 text-sm mt-1">Message: {quote.message}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" data-testid="contacts-content">
            <div className="glass-card p-6">
              <h2 className="text-2xl font-bold mb-6">Contact Messages</h2>
              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <p className="text-gray-400">No contact messages yet</p>
                ) : (
                  contacts.map((contact) => (
                    <div key={contact.id} data-testid={`contact-${contact.id}`} className="bg-gray-900 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-semibold">{contact.name}</p>
                          <p className="text-gray-400 text-sm">{contact.email} {contact.phone && `| ${contact.phone}`}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm mt-2">{contact.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" data-testid="gallery-content">
            <div className="glass-card p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Gallery Management</h2>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button data-testid="add-gallery-btn" className="bg-red-600 hover:bg-red-700">
                      <Upload className="mr-2 h-4 w-4" />
                      Add Gallery Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-gallery-dialog" className="bg-gray-900 border-gray-700 text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Add New Gallery Item</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Title *</Label>
                        <Input
                          data-testid="gallery-title-input"
                          value={newGalleryItem.title}
                          onChange={(e) => setNewGalleryItem(prev => ({ ...prev, title: e.target.value }))}
                          className="bg-gray-800 border-gray-700"
                        />
                      </div>
                      <div>
                        <Label>Category *</Label>
                        <Input
                          data-testid="gallery-category-input"
                          value={newGalleryItem.category}
                          onChange={(e) => setNewGalleryItem(prev => ({ ...prev, category: e.target.value }))}
                          className="bg-gray-800 border-gray-700"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          data-testid="gallery-description-input"
                          value={newGalleryItem.description}
                          onChange={(e) => setNewGalleryItem(prev => ({ ...prev, description: e.target.value }))}
                          className="bg-gray-800 border-gray-700"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Before Image *</Label>
                          <input
                            type="file"
                            data-testid="gallery-before-image-input"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'before_image')}
                            className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-red-600 file:text-white hover:file:bg-red-700"
                          />
                        </div>
                        <div>
                          <Label>After Image *</Label>
                          <input
                            type="file"
                            data-testid="gallery-after-image-input"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'after_image')}
                            className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-red-600 file:text-white hover:file:bg-red-700"
                          />
                        </div>
                      </div>
                      <Button
                        data-testid="submit-gallery-btn"
                        onClick={handleAddGalleryItem}
                        disabled={isAddingGallery}
                        className="w-full bg-red-600 hover:bg-red-700"
                      >
                        {isAddingGallery ? 'Adding...' : 'Add Gallery Item'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gallery.length === 0 ? (
                  <p className="text-gray-400">No gallery items yet</p>
                ) : (
                  gallery.map((item) => (
                    <div key={item.id} data-testid={`gallery-item-${item.id}`} className="bg-gray-900 p-4 rounded-lg">
                      <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">{item.description}</p>
                      <span className="inline-block px-2 py-1 bg-red-900/30 text-red-500 text-xs rounded mb-3">
                        {item.category}
                      </span>
                      <Button
                        data-testid={`delete-gallery-${item.id}`}
                        onClick={() => handleDeleteGalleryItem(item.id)}
                        variant="destructive"
                        size="sm"
                        className="w-full"
                      >
                        Delete
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
